import { getStatementRollingFeeData, getStatementSetupFeeData, getVisibleSetupFeeEntries, hasVisibleRollingReserveFees, hasVisibleStatementFees, isVisibleFeeValue, parseUtcDate } from "src/tools/statementPresentation";
import type { IStatementFees } from "src/tools/statementPresentation";
import { displayText } from "../../locales/display";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { pdf } from "@react-pdf/renderer";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import { IconEdit } from "@ui/icons/IconEdit";
import Input from "@ui/input/Input";
import Panel from "@ui/panel/Panel";
import Pre from "@ui/pre/Pre";
import { Select } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { Spinner } from "@ui/spinner/Spinner";
import { Table } from "@ui/table/Table";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import { Typo } from "@ui/text/Typo";
import { Signal } from "badmfck-signal";
import Decimal from "decimal.js";
import { saveAs } from "file-saver";
import { ArrowLeftRight, Braces, FileText, History, X } from "lucide-react";
import styled from "styled-components";
import { STATEMENT_TOTALS_COLUMNS, statementTotalsRows, statementTotalsCsv, statementTotalsXlsx } from "src/tools/statementTotalsExport";
import React, { createElement, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { GD } from "src/GD";
import { useStatementDetails } from "src/hooks/useStatementDetails";
import { useHasPermission } from "src/hooks/tools";
import type { IStatementRow, IStatementTotals, IStatementReport, IStatementDetailsData } from "src/types/statement";
import { getStatementTypeLabel, isTransitionStatement } from "src/tools/statements";
import type { IClient } from "src/services/ClientService";
import { StatementPDF, type StatementPDFProps } from "src/tools/pdf/StatementPDF";
import { formatAmount } from "src/tools/tools";
import { HoverTooltip } from "../conponents/HoverTooltip";
import { S_REPORT_POPUP } from "./ReportPopup";
import { StatementTransactionsTab } from "./StatementTransactionsTab";
import { StatementEventsTab } from "./StatementEventsTab";
import { TransactionNavigationContext } from "../transactionNavigationContext";

export const S_STATEMENT_POPUP = new Signal<(Pick<IStatementRow, "id"> & Partial<IStatementRow>) | null>();
export const S_STATEMENT_CHANGED = new Signal<IStatementRow>();

export const StatementPopup = () => {
	useLocale();
	const signalData = S_STATEMENT_POPUP.use();
	if (!signalData) return null;
	return <StatementDialog statementID={signalData.id} portal={false} onClose={() => S_STATEMENT_POPUP.invoke(null)} onStatementChanged={statement => S_STATEMENT_POPUP.invoke(statement)} />;
};

export const StatementDialog = ({ statementID, onClose, onStatementChanged, portal = true }: { statementID: number; onClose: () => void; onStatementChanged?: (statement: IStatementRow) => void; portal?: boolean }) => {
	useLocale();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);
	const canViewStatements = useHasPermission("statements.view");
	const allowStatementStatusEdit = canEditStatementStatus(project, user);
	const popup = useRef<HTMLDivElement>(null);
	const validId = Number.isSafeInteger(statementID) && statementID > 0;

	useEffect(() => {
		if (!canViewStatements || !validId) return;
		const previousFocus = document.activeElement;
		popup.current?.focus({ preventScroll: true });
		return () => { if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true }); };
	}, [statementID, canViewStatements, validId]);

	if (!canViewStatements || !validId) return null;
	const dialog = <div ref={popup} role="dialog" aria-modal="true" aria-label={msg("templateStatement_0efb6d80", { v0: statementID })} tabIndex={-1} onKeyDown={event => {
		if (event.key === "Escape") { event.stopPropagation(); onClose(); }
	}}>
		<StatementPopupContent key={`${project?.id}:${user?.id}:${statementID}`} statementID={statementID} canEditStatus={allowStatementStatusEdit} onClose={onClose} onStatementChanged={onStatementChanged} />
	</div>;
	return portal ? createPortal(dialog, document.body) : dialog;
};

const STATEMENT_LOADER_DELAY_MS = 250;

const StatementHeader = styled.header`
	display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; gap: 16px;
	padding: 18px 20px; border-bottom: 1px solid #eaecf0; background: #fff;
	font-family: "Euclid Circular B", sans-serif;
	.context { margin-bottom: 3px; color: #667085; font-size: 11px; line-height: 1.5; }
	h2 { margin: 0; color: #182230; font-size: 21px; font-weight: 600; line-height: 1.3; }
	button { display: grid; place-items: center; flex-shrink: 0; width: 34px; height: 34px; padding: 0; border: 1px solid #d0d5dd; border-radius: 8px; background: #fff; color: #667085; cursor: pointer; }
	button:hover { color: #182230; background: #f2f4f7; }
	button:focus-visible { outline: 2px solid #84adff; outline-offset: 2px; }
`;

const StatementLoader = () => {
	useLocale();
	const [visible, setVisible] = useState(false);
	useEffect(() => {
		const timer = window.setTimeout(() => setVisible(true), STATEMENT_LOADER_DELAY_MS);
		return () => window.clearTimeout(timer);
	}, []);

	return (
		<VBox center sx={{ flex: 1, minHeight: "200px" }}>
			{visible && (
				<div role="status" aria-live="polite">
					<VBox center gap="12px">
						<Spinner size={28} color="#64748b" />
						<Typo variant="medium" sx={{ color: "#64748b" }}>{msg("loadingStatement")}</Typo>
					</VBox>
				</div>
			)}
		</VBox>
	);
};

const StatementPopupContent = ({ statementID, canEditStatus, onClose, onStatementChanged }: { statementID: number; canEditStatus: boolean; onClose: () => void; onStatementChanged?: (statement: IStatementRow) => void }) => {
	useLocale();
	const { state, reload } = useStatementDetails(statementID);
	const canViewEvents = useHasPermission("nav.events");

	return (
		<Panel
			sx={{ width: "94vw", maxWidth: "1200px", height: "85vh", padding: 0, borderRadius: "12px", backgroundColor: "#f5f7fa", overflow: "hidden" }}
			decorator="absolute"
			onClose={onClose}>
			<StatementHeader>
				<div><div className="context">{msg("financial")}</div><h2>{msg("templateStatement_0efb6d80", { v0: statementID })}</h2></div>
				<button type="button" aria-label={msg("close")} title={msg("close")} onClick={onClose}><X size={18} aria-hidden="true" /></button>
			</StatementHeader>
			<Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
				{state.status === "loading" && <StatementLoader />}
				{state.status === "error" && (
					<VBox center gap="16px" sx={{ flex: 1 }}>
						<Error error={state.message} />
						<Button title={msg("tryAgain")} variant="secondarySmall" onClick={reload} />
					</VBox>
				)}
				{state.status === "ready" && (
					<Tabs variant="modern" spaceBottom={20} spaceLeft={20} background="#fff">
						<TabItem title={msg("details")} icon={<FileText size={16} />}>
							<VBox sx={{ flex: 1, minHeight: 0, padding: "0 20px 20px" }}>
								<StatementDetails
									signalData={state.statement}
									data={state.statement.data ?? undefined}
									clientModel={state.client}
									totals={state.statement.totals ?? undefined}
									fees={state.statement.data?.fees}
									reports={state.statement.data?.reports ?? []}
									canEditStatus={canEditStatus}
									onReload={reload}
									onStatementChanged={onStatementChanged}
								/>
							</VBox>
						</TabItem>
						<TabItem title={msg("transactions")} icon={<ArrowLeftRight size={16} />}>
							<Box sx={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden", padding: "0 20px 20px" }}>
								<StatementTransactionsTab key={statementID} statementId={statementID} />
							</Box>
						</TabItem>
						{canViewEvents && <TabItem title={msg("events")} icon={<History size={16} />}>
							<StatementEventsTab key={statementID} statementId={statementID} />
						</TabItem>}
						<TabItem title={msg("raw_ac0562bb")} icon={<Braces size={16} />}>
							<Box sx={{ flex: 1, minHeight: 0, overflow: "auto", padding: "0 20px 20px" }}><Pre data={state.response} /></Box>
						</TabItem>
					</Tabs>
				)}
			</Box>
		</Panel>
	);
};

// ── Statement Details tab content ──

const STATEMENT_STATUSES = ["created", "processing", "completed", "rejected"];
const STATEMENT_STATUS_EDIT_ROLE_IDS = [1, 4];

const canEditStatementStatus = (project: any, user: any) => {
	if (!project || !user) return false;

	const userProject = user.projects?.find((item: any) => item.id === project.id);
	//console.log("Checking statement status edit permissions for user:", { userProject });
	return Boolean(userProject?.roles?.some((role: any) => STATEMENT_STATUS_EDIT_ROLE_IDS.includes(Number(role.id))));
};

const getStatementStatusColor = (status: string): string => {
	switch (status?.toLowerCase()) {
		case "completed":
		case "calculated":
			return "#15803d";
		case "rejected":
			return "#b91c1c";
		case "created":
			return "#64748b";
		case "processing":
			return "#b45309";
		default:
			return "#64748b";
	}
};

interface IStatementStatusChange {
	id: number;
	status: string;
	onSuccess: (status: string, reason?: string) => void;
}

const StatementStatusChangePopup = ({ canEditStatus, data, onClose }: { canEditStatus: boolean; data: IStatementStatusChange | null; onClose: () => void }) => {
	useLocale();
	const popup = useRef<HTMLDivElement>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<IError>();
	const [status, setStatus] = useState("");
	const [reason, setReason] = useState<{ value: string }>({ value: "" });

	useEffect(() => {
		if (data) {
			setStatus(data.status);
			setReason({ value: "" });
			setError(undefined);
		}
	}, [data]);
	useEffect(() => {
		if (!data || !canEditStatus) return;
		const previousFocus = document.activeElement;
		popup.current?.focus({ preventScroll: true });
		return () => { if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true }); };
	}, [data, canEditStatus]);

	const closePopup = () => {
		onClose();
		setError(undefined);
		setReason({ value: "" });
	};

	useEffect(() => {
		if (data && !canEditStatus) {
			closePopup();
		}
	}, [canEditStatus, data]);

	if (!data || !canEditStatus) return null;

	const handleSubmit = async () => {
		setError(undefined);
		if (status === "rejected" && !reason.value.trim()) {
			setError({ message: msg("pleaseSpecifyReason"), code: -1 });
			return;
		}
		if (status === data.status) {
			setError({ message: msg("pleaseSelectADifferentStatus"), code: -1 });
			return;
		}
		setBusy(true);
		const res = await GD.REQ_API.request({
			method: "statements/status/change",
			data: { id: data.id, status, reason: reason.value },
		});
		setBusy(false);
		if (res?.error) {
			setError(res.error);
		} else {
			data.onSuccess(status, reason.value);
			closePopup();
		}
	};

	return (
		<div ref={popup} role="dialog" aria-modal="true" aria-label={msg("changeStatementStatus")} tabIndex={-1} onKeyDown={event => {
			if (event.key === "Escape") { event.stopPropagation(); closePopup(); }
		}}>
		<Panel sx={{ maxWidth: "500px", minWidth: "400px" }} decorator="absolute" busy={busy} growContent label={msg("changeStatementStatus")} onClose={closePopup}>
			<Spacer size="16px" />
			<HBox gap="12px" sx={{ alignItems: "center", justifyContent: "center" }}>
				<Box
					sx={{
						padding: "6px 16px",
						borderRadius: "6px",
						backgroundColor: `${getStatementStatusColor(data.status)}12`,
						color: getStatementStatusColor(data.status),
						border: `1px solid ${getStatementStatusColor(data.status)}25`,
						fontWeight: 600,
						fontSize: "14px",
					}}>
					{data.status}
				</Box>
				<Typo sx={{ fontSize: "18px", opacity: 0.4 }}>{"\u2192"}</Typo>
				<Box
					sx={{
						padding: "6px 16px",
						borderRadius: "6px",
						backgroundColor: `${getStatementStatusColor(status)}12`,
						color: getStatementStatusColor(status),
						border: `1px solid ${getStatementStatusColor(status)}25`,
						fontWeight: 600,
						fontSize: "14px",
					}}>
					{status}
				</Box>
			</HBox>
			<Spacer size="20px" />
			<VBox gap="12px">
				<Select size="small" value={status} values={STATEMENT_STATUSES} onChange={setStatus} />
				{status === "rejected" && <Input label={msg("reason_f81ab834")} data={reason} name="value" />}
			</VBox>
			<Spacer size="20px" />
			<VBox gap="12px">
				<Error error={error} />
				<Button title={msg("submit_155f816c")} onClick={handleSubmit} />
			</VBox>
		</Panel>
		</div>
	);
};

const formatSetupFeeValue = (key: string, value: string | number) => {
	if (typeof value !== "number") return String(value);
	if (key === "fx_fee") return `${value}%`;
	if (key === "t_cut_off_day") return String(value);
	return formatAmount(value);
};

const formatUtcDate = (value?: string | Date | null) => {
	const date = parseUtcDate(value);
	return date ? new Intl.DateTimeFormat(undefined, { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" }).format(date) : "";
};

const formatUtcDateTime = (value?: string | Date | null) => {
	const date = parseUtcDate(value);
	return date
		? new Intl.DateTimeFormat(undefined, {
				timeZone: "UTC",
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
				hour12: false,
			}).format(date)
		: "";
};

const statementCardSx = {
	backgroundColor: "#fff",
	border: "1px solid #d0d5dd",
	borderRadius: "10px",
	padding: "18px 20px",
	boxShadow: "0 1px 2px rgba(16,24,40,.05)",
	minWidth: 0,
};

const StatementSection = ({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) => { useLocale(); return ((
	<VBox gap="12px" sx={statementCardSx}>
		<HBox gap="12px" sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
			<Typo variant="medium" sx={{ color: "#374151", fontSize: "18px", lineHeight: "26px", fontWeight: 600 }}>{title}</Typo>
			{action}
		</HBox>
		{children}
	</VBox>
)); };

const StatementInfoRow = ({ label, value, fullWidth }: { label: string; value?: React.ReactNode; fullWidth?: boolean }) => { useLocale(); return ((
	<HBox gap="12px" sx={{ padding: "10px 0", minWidth: 0, gridColumn: fullWidth ? "1 / -1" : undefined, borderBottom: "1px solid #f0f0f0", alignItems: "baseline", "&:last-child": { borderBottom: "none", paddingBottom: 0 } }}>
		<Typo variant="small" sx={{ color: "#6b7280", width: "130px", flexShrink: 0, fontSize: "13px", lineHeight: "20px" }}>{label}</Typo>
		<Box sx={{ flex: 1, minWidth: 0, color: "#1f2937", fontSize: "14px", lineHeight: "20px", fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>
			{value === undefined || value === null || value === "" ? "—" : value}
		</Box>
	</HBox>
)); };

const StatementField = ({ label, value }: { label: string; value: React.ReactNode }) => { useLocale(); return ((
	<VBox gap="5px" sx={{ minWidth: 0 }}>
		<Typo variant="small" sx={{ color: "#6b7280", fontSize: "13px", lineHeight: "20px" }}>{label}</Typo>
		<Typo variant="medium" sx={{ color: "#1f2937", fontSize: "14px", lineHeight: "20px", fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>{value}</Typo>
	</VBox>
)); };

const StatementTransitionDetails = ({ data }: { data?: IStatementDetailsData }) => {
	useLocale();
	const formatCount = (value?: number | null) => value === undefined || value === null ? msg("notAvailable") : value.toLocaleString();
	const fields = [
		{ label: msg("transactionsProcessed"), value: formatCount(data?.counts?.txn) },
		{ label: msg("rollingReserveReleases"), value: formatCount(data?.counts?.release) },
		{ label: msg("settlementAfterExclusive"), value: formatUtcDateTime(data?.window?.settlement_after) || msg("notAvailable") },
		{ label: msg("settlementThroughInclusive"), value: formatUtcDateTime(data?.window?.settlement_to) || msg("notAvailable") },
		{ label: msg("transactionsCreatedBefore"), value: formatUtcDateTime(data?.window?.created_before) || msg("notAvailable") },
	];

	return (
		<VBox gap="12px" sx={{ padding: "16px 20px", backgroundColor: "#f0f7ff", border: "1px solid #93c5fd", borderLeft: "3px solid #3b82f6", borderRadius: "10px" }}>
			<HBox gap="12px" sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
				<Typo variant="medium" sx={{ color: "#1e3a5f", fontSize: "18px", lineHeight: "26px", fontWeight: 600 }}>{msg("transitionStatement")}</Typo>
				<Typo variant="small" sx={{ color: "#64748b", fontSize: "13px" }}>{msg("allTimesAreUtc")}</Typo>
			</HBox>
			<Typo variant="medium" sx={{ color: "#475569", fontSize: "14px", lineHeight: "20px" }}>{msg("thisTransitionStatementCoversTransactionsLeftOutsideRegularS_f9a18d7c")}</Typo>
			<Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", gap: "16px 24px" }}>
				{fields.map(field => <StatementField key={field.label} label={field.label} value={field.value} />)}
			</Box>
		</VBox>
	);
};

const StatementDetails = ({
	signalData,
	data,
	clientModel,
	totals,
	fees,
	reports,
	canEditStatus,
	onReload,
	onStatementChanged,
}: {
	signalData: IStatementRow;
	data: IStatementDetailsData | undefined;
	clientModel: IClient | undefined;
	totals: Record<string, IStatementTotals> | undefined;
	fees: IStatementFees | undefined;
	reports: IStatementReport[];
	canEditStatus: boolean;
	onReload: () => void;
	onStatementChanged?: (statement: IStatementRow) => void;
}) => {
	useLocale();
	const [statusChange, setStatusChange] = useState<IStatementStatusChange | null>(null);
	const showSetupTotals = totals ? hasNonZeroTotalsColumn(totals, "setup") : false;
	const showMonthlyTotals = totals ? hasNonZeroTotalsColumn(totals, "monthly") : false;
	const statementType = signalData.type?.trim() || (fees ? getStatementSetupFeeData(fees)?.statement_period : undefined);
	const isDaily = String(statementType ?? "").trim().toLowerCase() === "daily";
	const detailFields = [
		{ label: msg("clientName"), value: data?.client_name ?? signalData.client_name ?? clientModel?.name },
		{ label: msg("statementId"), value: signalData.id },
		{ label: msg("clientId_8726db01"), value: signalData.client_id },
		{ label: msg("type"), value: statementType ? getStatementTypeLabel(statementType) : undefined },
		{ label: msg("registrationNumber"), value: clientModel?.registration_number },
		{ label: isDaily ? msg("date_99c40ab4") : msg("period_6e795d4d"), value: isDaily
			? formatUtcDate(signalData.period_start || signalData.period_end)
			: signalData.period_start && signalData.period_end ? `${formatUtcDate(signalData.period_start)} – ${formatUtcDate(signalData.period_end)}` : undefined },
		{ label: msg("rejectedBy"), value: signalData.rejected_by },
		{ label: msg("calculatedUtc"), value: formatUtcDateTime(signalData.calculated_at) },
	].filter(field => field.value !== undefined && field.value !== null && String(field.value).trim() !== "");

	return (
		<VBox sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
			<VBox gap="14px" sx={{ overflowY: "auto", minHeight: 0, flex: 1, padding: "2px 3px 4px" }}>
				<StatementSection title={msg("statementDetails")} action={
					<VBox sx={{ alignItems: "flex-end", gap: "6px", paddingTop: "2px" }}>
						<HBox
							sx={{
								alignItems: "stretch",
								borderRadius: "8px",
								overflow: "hidden",
								backgroundColor: `${getStatementStatusColor(signalData.status)}12`,
								border: `1px solid ${getStatementStatusColor(signalData.status)}25`,

							}}>
							<Box sx={{ padding: "7px 16px", display: "flex", alignItems: "center" }}>
								<Typo sx={{ color: getStatementStatusColor(signalData.status), fontWeight: 600, fontSize: "13px", whiteSpace: "nowrap", textTransform: "capitalize" }}>
									{msg("status_4889951f")}
									{displayText(signalData.status)}
								</Typo>
							</Box>
							{canEditStatus && (
								<Box
									title={msg("changeStatementStatus")}
									onClick={() =>
										setStatusChange({
											id: signalData.id,
											status: signalData.status,
											onSuccess: (status, reason) => {
												const updatedStatement = {
													...signalData,
													status,
													rejected_reason:
														status === "rejected" ? (reason ?? signalData.rejected_reason) : signalData.rejected_reason,
												};

												onStatementChanged?.(updatedStatement);
												S_STATEMENT_CHANGED.invoke(updatedStatement);
												onReload();
											},
										})
									}
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										padding: "0 10px",
										borderLeft: `1px solid ${getStatementStatusColor(signalData.status)}25`,
										cursor: "pointer",
										transition: "background-color 0.2s",
										"&:hover": { backgroundColor: `${getStatementStatusColor(signalData.status)}18` },
									}}>
									<IconEdit color={getStatementStatusColor(signalData.status)} />
								</Box>
							)}
						</HBox>
					</VBox>
				}>
					<Box sx={{
						display: "grid",
						gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
						columnGap: "32px",
						"@media (min-width: 801px)": {
							...(!signalData.rejected_reason?.trim() && detailFields.length % 2 === 0 ? {
								"& > div:nth-last-child(2)": { borderBottom: "none", paddingBottom: 0 },
							} : {}),
						},
						"@media (max-width: 800px)": { gridTemplateColumns: "minmax(0, 1fr)" },
					}}>
						{detailFields.map((field, index) => (
							<StatementInfoRow key={field.label} label={field.label} value={field.value} fullWidth={index === detailFields.length - 1 && detailFields.length % 2 === 1} />
						))}
						{signalData.rejected_reason?.trim() && <StatementInfoRow label={msg("rejectionReason")} value={<span style={{ color: "#b91c1c" }}>{signalData.rejected_reason}</span>} fullWidth />}
					</Box>
				</StatementSection>
				<StatementStatusChangePopup canEditStatus={canEditStatus} data={statusChange} onClose={() => setStatusChange(null)} />
				{isTransitionStatement(signalData) && <StatementTransitionDetails data={data} />}

				{totals && Object.keys(totals).length > 0 && (
					<StatementSection title={msg("totals")}>
						<TotalsTable totals={totals} showSetup={showSetupTotals} showMonthly={showMonthlyTotals} />
					</StatementSection>
				)}
				{hasVisibleStatementFees(fees) && fees && <FeesBlock fees={fees} />}
				{reports.length > 0 && <MerchantChannelBreakdown reports={reports} />}
				{reports.length > 0 && <DailyReports reports={reports} clientId={signalData.client_id} />}

			</VBox>
			<Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap", flexShrink: 0, padding: "12px 3px 0", marginTop: "auto" }}>
				{totals && Object.keys(totals).length > 0 && (["csv", "xlsx"] as const).map(format => (
					<Button key={format} title={msg("templateDownloadTotalsAs_9c4b7533", { v0: format === "csv" ? "CSV" : "Excel" })} variant="secondarySmall" onClick={() => {
						const rows = statementTotalsRows(signalData, clientModel?.name);
						const blob = format === "csv"
							? new Blob([statementTotalsCsv(rows)], { type: "text/csv;charset=utf-8" })
							: new Blob([new Uint8Array(statementTotalsXlsx(rows)).buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
						saveAs(blob, `statement_${signalData.id}_totals.${format}`);
					}} />
				))}
				<DownloadPdfButton
					signalData={signalData}
					data={data}
					registrationNumber={clientModel?.registration_number}
					totals={totals}
					fees={fees}
					reports={reports}
				/>
			</Box>
		</VBox>
	);
};

// ── Download PDF button ──

const DownloadPdfButton = ({
	signalData,
	data,
	registrationNumber,
	totals,
	fees,
	reports,
}: {
	signalData: IStatementRow;
	data: IStatementDetailsData | undefined;
	registrationNumber?: string | number;
	totals: Record<string, IStatementTotals> | undefined;
	fees: IStatementFees | undefined;
	reports: IStatementReport[];
}) => {
	useLocale();
	const [busy, setBusy] = useState(false);

	const handleDownload = async () => {
		setBusy(true);
		try {
			const props: StatementPDFProps = {
				statementId: signalData.id,
				clientName: data?.client_name ?? String(signalData.client_id),
				clientId: signalData.client_id,
				registrationNumber,
				clientStatus: data?.client_status,
				status: signalData.status,
				periodStart: signalData.period_start,
				periodEnd: signalData.period_end,
				calculatedAt: signalData.calculated_at,
				rejectedReason: signalData.rejected_reason,
				rejectedBy: signalData.rejected_by,
				totals,
				fees,
				reports,
				isPayswix: signalData.project_id === 3,
			};

			const pdfDocument = createElement(StatementPDF, props) as unknown as Parameters<typeof pdf>[0];
			const blob = await pdf(pdfDocument).toBlob();
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `statement_${signalData.id}.pdf`;
			a.click();
			URL.revokeObjectURL(url);
		} catch (e) {
			console.error("PDF generation failed:", e);
		}
		setBusy(false);
	};

	return (
		<HBox>
			<Button title={busy ? msg("generating") : msg("downloadAsPdf")} onClick={handleDownload} disabled={busy} />
		</HBox>
	);
};

const hasNonZeroTotalsColumn = (totals: Record<string, IStatementTotals>, key: "setup" | "monthly") =>
	Object.values(totals).some(item => Number(item[key] ?? 0) !== 0);

const TotalsTable = ({ totals, showSetup, showMonthly }: { totals: Record<string, IStatementTotals>; showSetup: boolean; showMonthly: boolean }) => {
	useLocale();
	const columns = STATEMENT_TOTALS_COLUMNS.filter(column => (column.value !== "setup" || showSetup) && (column.value !== "monthly" || showMonthly));
	const rows = Object.entries(totals).map(([currency, values]) => ({ ...values, currency }));

	return (
		<Box sx={{ minWidth: 0, width: "100%", overflowX: "auto" }}>
			<Table<IStatementTotals & { currency: string }>
				headPadding="10px 12px"
				cellPadding="10px 12px"
				headSx={statementTableHeadSx}
				sx={{ width: "max-content", minWidth: "100%" }}
				headers={columns.map(column => ({ value: column.value, gridColumn: column.value === "currency" ? "max-content" : "auto", sx: { textAlign: column.value === "currency" ? "left" : "right" } }))}
				order={columns.map(column => column.value)}
				headerFactory={value => ({
					content: <Typo sx={{ color: "#4b5563", fontSize: "13px", lineHeight: "20px", fontWeight: 600, whiteSpace: "nowrap" }}>{columns.find(column => column.value === value)?.label}</Typo>,
				})}
				cellFactory={(row, _cell, _index, _key, name) => {
					const column = columns.find(item => item.value === name);
					if (!column) return { content: null };
					const isCurrency = column.value === "currency";
					const value = column.value === "currency" ? row.currency : formatAmount(row[column.value] ?? 0);
					return {
						content: <Typo sx={{ ...tableCellSx, textAlign: isCurrency ? "left" : "right", fontWeight: isCurrency ? 600 : 400 }}>{value}</Typo>,
					};
				}}
				data={rows}
			/>
		</Box>
	);
};

// ── Fees info block ──

const FeesBlock = ({ fees }: { fees: IStatementFees }) => {
	useLocale();
	const setup = fees.setup;
	const rollings = fees.rollings;
	const rollingData = getStatementRollingFeeData(fees);
	const setupEntries = getVisibleSetupFeeEntries(fees);
	const hasRollings = hasVisibleRollingReserveFees(fees);
	const rollingReserved = rollingData?.reserved;
	const rollingPeriod = rollingData?.period;
	const rollingVolume = rollingData?.volume;
	const rollingCap = rollingData?.cap;

	if (setupEntries.length === 0 && !hasRollings) return null;

	return (
		<Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: "14px" }}>
			{setup && setupEntries.length > 0 && (
				<StatementSection title={msg("setupFees")}>
					<VBox>
						{setupEntries.map(([key, val]) => <StatementInfoRow key={key} label={getStatementTypeLabel(key)} value={formatSetupFeeValue(key, val)} />)}
					</VBox>
				</StatementSection>
			)}
			{hasRollings && rollings && (
				<StatementSection title={msg("rollingReserve")}>
					<VBox>
						{rollings.activated_from && <StatementInfoRow label={msg("activeFromUtc")} value={formatUtcDateTime(rollings.activated_from)} />}
						{isVisibleFeeValue(rollingReserved) && <StatementInfoRow label={msg("reserved_3385ffe6")} value={`${rollingReserved}%`} />}
						{isVisibleFeeValue(rollingPeriod) && <StatementInfoRow label={msg("period_6e795d4d")} value={msg("templateDays_4ab2bbbf", { v0: rollingPeriod })} />}
						{isVisibleFeeValue(rollingVolume) && <StatementInfoRow label={msg("volume_b10fb966")} value={rollingVolume} />}
						{isVisibleFeeValue(rollingCap) && <StatementInfoRow label={msg("cap_1257ac04")} value={rollingCap ? "Yes" : "No"} />}
					</VBox>
				</StatementSection>
			)}
		</Box>
	);
};

// ── Daily Reports wrapper ──

const DailyReports = ({ reports, clientId }: { reports: IStatementReport[]; clientId: number }) => { useLocale(); return ((
	<StatementSection title={msg("templateDailyReports_e5f375cf", { v0: reports.length })}>
		<ReportsTable reports={reports} clientId={clientId} />
	</StatementSection>
)); };

// ── Daily reports table ──

interface IReportRow {
	id: number;
	date: string;
	ccy: string;
	success: string;
	amount: string;
	refunds: string;
	chargebacks: string;
	oct: string;
	failed: string;
	fees: string;
	rollings: string;
	release_rollings: string;
	adj_in: string;
	adj_out: string;
	net: string;
	hasTotals: boolean;
	isFirstInGroup: boolean;
	isLastInGroup: boolean;
}

const tableCellSx = {
	color: "#1f2937",
	fontVariantNumeric: "tabular-nums",
	fontSize: "14px",
	whiteSpace: "nowrap" as const,
};

const statementTableHeadSx = {
	backgroundColor: "#f9fafb",
	color: "#6b7280",
	borderBottom: "1px solid #e5e7eb",
};

const stickyHeadSx: React.CSSProperties = {
	position: "sticky",
	top: 0,
	zIndex: 2,
};

const stickyHeadRightSx: React.CSSProperties = {
	...stickyHeadSx,
	textAlign: "right",
};

const reportHeaderLabels: Record<string, string> = {
	amount: "Turnover",
};

interface IMerchantChannelMetrics {
	success: Decimal;
	amount: Decimal;
	refunds: Decimal;
	chargebacks: Decimal;
	oct: Decimal;
	octCount: number;
	failed: Decimal;
	fees: Decimal;
	rollings: Decimal;
	releaseRollings: Decimal;
	adjustmentIn: Decimal;
	adjustmentOut: Decimal;
	net: Decimal;
}

interface IMerchantChannelGroup {
	key: string;
	name: string;
	channels: {
		key: string;
		name: string;
		currencies: { currency: string; metrics: IMerchantChannelMetrics }[];
	}[];
}

interface IMerchantChannelRow {
	id: string;
	channel: string;
	ccy: string;
	success: string;
	amount: string;
	refunds: string;
	chargebacks: string;
	failed: string;
	fees: string;
	rollings: string;
	release_rollings: string;
	adj_in: string;
	adj_out: string;
	net: string;
	isFirstInGroup: boolean;
	isLastInGroup: boolean;
}

const emptyMerchantChannelMetrics = (): IMerchantChannelMetrics => ({
	success: new Decimal(0),
	amount: new Decimal(0),
	refunds: new Decimal(0),
	chargebacks: new Decimal(0),
	oct: new Decimal(0),
	octCount: 0,
	failed: new Decimal(0),
	fees: new Decimal(0),
	rollings: new Decimal(0),
	releaseRollings: new Decimal(0),
	adjustmentIn: new Decimal(0),
	adjustmentOut: new Decimal(0),
	net: new Decimal(0),
});

const metricDecimal = (value: unknown) => {
	if (typeof value !== "number" && typeof value !== "string") return new Decimal(0);
	try {
		const decimal = new Decimal(value);
		return decimal.isFinite() ? decimal : new Decimal(0);
	} catch {
		return new Decimal(0);
	}
};

const formatDecimalAmount = (value: Decimal) => {
	const decimalPlaces = value.decimalPlaces();
	return decimalPlaces < 2 ? value.toFixed(2) : value.toString();
};

const aggregateMerchantChannels = (reports: IStatementReport[]): IMerchantChannelGroup[] => {
	type TCurrencyAggregate = { currency: string; metrics: IMerchantChannelMetrics };
	type TChannelAggregate = { key: string; name: string; currencies: Map<string, TCurrencyAggregate> };
	type TMerchantAggregate = { key: string; name: string; channels: Map<string, TChannelAggregate> };

	const merchants = new Map<string, TMerchantAggregate>();

	for (const dailyReport of reports) {
		for (const merchant of dailyReport.data.merchants ?? []) {
			const merchantName = merchant.name?.trim() || String(merchant.id ?? msg("unknownMerchant"));
			const merchantKey = String(merchant.id ?? merchantName);
			let merchantAggregate = merchants.get(merchantKey);
			if (!merchantAggregate) {
				merchantAggregate = { key: merchantKey, name: merchantName, channels: new Map() };
				merchants.set(merchantKey, merchantAggregate);
			}

			for (const channelReport of merchant.reports ?? []) {
				if (channelReport.channel_id === 0 || channelReport.channel_id === "0") continue;

				const channelName = channelReport.channel_name?.trim() || channelReport.channel_alias?.trim() || String(channelReport.channel_id ?? msg("unknownChannel"));
				const channelKey = String(channelReport.channel_id ?? channelName);
				let channelAggregate = merchantAggregate.channels.get(channelKey);
				if (!channelAggregate) {
					channelAggregate = { key: channelKey, name: channelName, currencies: new Map() };
					merchantAggregate.channels.set(channelKey, channelAggregate);
				}

				for (const [currency, total] of Object.entries(channelReport.total ?? {})) {
					let currencyAggregate = channelAggregate.currencies.get(currency);
					if (!currencyAggregate) {
						currencyAggregate = { currency, metrics: emptyMerchantChannelMetrics() };
						channelAggregate.currencies.set(currency, currencyAggregate);
					}

					const metrics = currencyAggregate.metrics;
					metrics.success = metrics.success.plus(metricDecimal(total.count_success));
					metrics.amount = metrics.amount.plus(metricDecimal(total.total_amount));
					metrics.refunds = metrics.refunds.plus(metricDecimal(total.total_refunds));
					metrics.chargebacks = metrics.chargebacks.plus(metricDecimal(total.total_chargebacks));
					metrics.oct = metrics.oct.plus(metricDecimal(total.total_oct));
					metrics.octCount += Number(total.count_oct) || 0;
					metrics.failed = metrics.failed.plus(metricDecimal(total.total_failed));
					metrics.fees = metrics.fees.plus(metricDecimal(total.total_fees));
					metrics.rollings = metrics.rollings.plus(metricDecimal(total.total_rollings));
					metrics.releaseRollings = metrics.releaseRollings.plus(metricDecimal(total.total_release_rollings));
					metrics.adjustmentIn = metrics.adjustmentIn.plus(metricDecimal(total.total_ajustment_in));
					metrics.adjustmentOut = metrics.adjustmentOut.plus(metricDecimal(total.total_ajustment_out));
					metrics.net = metrics.net.plus(metricDecimal(total.total_net));
				}
			}
		}
	}

	return [...merchants.values()]
		.map(merchant => ({
			key: merchant.key,
			name: merchant.name,
			channels: [...merchant.channels.values()]
				.map(channel => ({
					key: channel.key,
					name: channel.name,
					currencies: [...channel.currencies.values()].sort((a, b) => a.currency.localeCompare(b.currency)),
				}))
				.sort((a, b) => a.name.localeCompare(b.name)),
		}))
		.filter(merchant => merchant.channels.length > 0)
		.sort((a, b) => a.name.localeCompare(b.name));
};

// A channel is an OCT (payout) channel when any of its currency buckets recorded OCT transactions.
// OCT lives on a dedicated channel, so this cleanly separates payout channels from the rest.
const channelIsOct = (channel: IMerchantChannelGroup["channels"][number]) => channel.currencies.some(c => c.metrics.octCount > 0);

const MerchantChannelBreakdown = ({ reports }: { reports: IStatementReport[] }) => {
	useLocale();
	const merchants = aggregateMerchantChannels(reports);
	if (merchants.length === 0) return null;

	return (
		<StatementSection title={msg("merchantsAndChannels")}>
			<VBox gap="20px">
				{merchants.map(merchant => {
					const octChannels = merchant.channels.filter(channelIsOct);
					const regularChannels = merchant.channels.filter(c => !channelIsOct(c));
					return (
						<VBox key={merchant.key} gap="10px">
							<Typo variant="medium" sx={{ color: "#374151", fontSize: "16px", lineHeight: "24px", fontWeight: 600 }}>{merchant.name}</Typo>
							{regularChannels.length > 0 && <MerchantChannelsTable merchant={{ ...merchant, channels: regularChannels }} />}
							{octChannels.length > 0 && (
								<VBox gap="8px">
									<Typo variant="small" sx={{ color: "#6b7280", fontWeight: 600 }}>{msg("octPayouts")}</Typo>
									<OctChannelsTable channels={octChannels} />
								</VBox>
							)}
						</VBox>
					);
				})}
			</VBox>
		</StatementSection>
	);
};

interface IOctChannelRow {
	id: string;
	channel: string;
	ccy: string;
	count: string;
	paid_out: string;
	fees: string;
	net: string;
	isFirstInGroup: boolean;
	isLastInGroup: boolean;
}

// Dedicated OCT/payout table: shows how much LEFT the merchant (paid out) plus the fee, and the
// resulting net (what the accountant deducts). Turnover/refunds/chargebacks columns are irrelevant
// here — OCT channels only move money out — so this table keeps just the OCT-relevant figures.
const OctChannelsTable = ({ channels }: { channels: IMerchantChannelGroup["channels"] }) => {
	useLocale();
	const rows: IOctChannelRow[] = [];
	for (const channel of channels) {
		channel.currencies.forEach(({ currency, metrics }, index) => {
			rows.push({
				id: `${channel.key}:${currency}`,
				channel: channel.name,
				ccy: currency,
				count: String(metrics.octCount),
				paid_out: formatDecimalAmount(metrics.oct),
				fees: formatDecimalAmount(metrics.fees),
				net: formatDecimalAmount(metrics.net),
				isFirstInGroup: index === 0,
				isLastInGroup: index === channel.currencies.length - 1,
			});
		});
	}

	return (
		<Box sx={{ display: "flex", flex: 1, width: "100%", overflowX: "auto" }}>
			<Table<IOctChannelRow>
				headPadding="10px 12px"
				headSx={statementTableHeadSx}
				cellPadding="10px 12px"
				sx={{ width: "max-content", minWidth: "100%", fontVariantNumeric: "tabular-nums" }}
				headerFactory={val => {
					if (typeof val === "string") {
						const labels: Record<string, string> = { count: "TXNS", paid_out: msg("paidOut") };
						return {
							content: (
								<Typo
									variant="medium"
									sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.03em" }}>
									{(labels[val] ?? val).toUpperCase()}
								</Typo>
							),
						};
					}
					return { content: null };
				}}
				headers={[
					{ value: "channel", gridColumn: "minmax(280px, 1.5fr)", sx: stickyHeadSx },
					{ value: "ccy", gridColumn: "max-content", sx: stickyHeadSx },
					{ value: "count", gridColumn: "max-content", sx: stickyHeadRightSx },
					{ value: "paid_out", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "fees", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "net", gridColumn: "auto", sx: stickyHeadRightSx },
				]}
				order={["channel" as any, "ccy" as any, "count" as any, "paid_out" as any, "fees" as any, "net" as any]}
				gridTemplateColumns="minmax(280px, 1.5fr) max-content max-content auto auto auto"
				cellFactory={(row, _cell, _index, _key, name) => {
					const noBorder = !row.isLastInGroup ? { borderBottom: "1px solid transparent" } : {};
					if (name === "channel") {
						return {
							content: row.isFirstInGroup ? (
								<Typo sx={{ ...tableCellSx, fontFamily: "inherit", fontWeight: 600, display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>{row.channel}</Typo>
							) : null,
							sx: noBorder,
						};
					}
					if (name === "ccy") return { content: <Typo sx={{ ...tableCellSx, fontWeight: 700 }}>{row.ccy}</Typo>, sx: noBorder };
					if (name === "count") return { content: <Typo sx={{ ...tableCellSx, textAlign: "end", display: "block" }}>{row.count}</Typo>, sx: noBorder };
					if (name === "net") return { content: <Typo sx={{ ...tableCellSx, fontWeight: 600, textAlign: "end", display: "block", color: "#DC2626" }}>{row.net}</Typo>, sx: noBorder };
					const valueColumns: Record<string, keyof IOctChannelRow> = { paid_out: "paid_out", fees: "fees" };
					if (name && name in valueColumns) {
						return { content: <Typo sx={{ ...tableCellSx, textAlign: "end", display: "block" }}>{row[valueColumns[name]]}</Typo>, sx: noBorder };
					}
					return { content: null, sx: noBorder };
				}}
				data={rows}
			/>
		</Box>
	);
};

const MerchantChannelsTable = ({ merchant }: { merchant: IMerchantChannelGroup }) => {
	useLocale();
	const rows: IMerchantChannelRow[] = [];

	for (const channel of merchant.channels) {
		channel.currencies.forEach(({ currency, metrics }, index) => {
			rows.push({
				id: `${channel.key}:${currency}`,
				channel: channel.name,
				ccy: currency,
				success: metrics.success.toString(),
				amount: formatDecimalAmount(metrics.amount),
				refunds: formatDecimalAmount(metrics.refunds),
				chargebacks: formatDecimalAmount(metrics.chargebacks),
				failed: formatDecimalAmount(metrics.failed),
				fees: formatDecimalAmount(metrics.fees),
				rollings: formatDecimalAmount(metrics.rollings),
				release_rollings: formatDecimalAmount(metrics.releaseRollings),
				adj_in: formatDecimalAmount(metrics.adjustmentIn),
				adj_out: formatDecimalAmount(metrics.adjustmentOut),
				net: formatDecimalAmount(metrics.net),
				isFirstInGroup: index === 0,
				isLastInGroup: index === channel.currencies.length - 1,
			});
		});
	}

	return (
		<Box sx={{ display: "flex", flex: 1, width: "100%", overflowX: "auto" }}>
			<Table<IMerchantChannelRow>
				headPadding="10px 12px"
				headSx={statementTableHeadSx}
				cellPadding="10px 12px"
				sx={{ width: "max-content", minWidth: "100%", fontVariantNumeric: "tabular-nums" }}
				headerFactory={val => {
					if (typeof val === "string") {
						return {
							content: (
								<Typo
									variant="medium"
									sx={{
										fontWeight: 700,
										fontVariantNumeric: "tabular-nums",
										fontSize: "11px",
										textTransform: "uppercase",
										letterSpacing: "0.03em",
									}}>
									{(reportHeaderLabels[val] ?? val).toUpperCase()}
								</Typo>
							),
						};
					}
					return { content: null };
				}}
				headers={[
					{ value: "channel", gridColumn: "minmax(280px, 1.5fr)", sx: stickyHeadSx },
					{ value: "ccy", gridColumn: "max-content", sx: stickyHeadSx },
					{ value: "txns", gridColumn: "max-content", sx: stickyHeadRightSx },
					{ value: "amount", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "refunds", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "chargebacks", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "failed", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "rollings", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "released", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "fees", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "net", gridColumn: "auto", sx: stickyHeadRightSx },
				]}
				order={["channel" as any, "ccy" as any, "txns" as any, "amount" as any, "refunds" as any, "chargebacks" as any, "failed" as any, "rollings" as any, "released" as any, "fees" as any, "net" as any]}
				gridTemplateColumns="minmax(280px, 1.5fr) max-content max-content auto auto auto auto auto auto auto auto"
				cellFactory={(row, _cell, _index, _key, name) => {
					const noBorder = !row.isLastInGroup ? { borderBottom: "1px solid transparent" } : {};
					if (name === "channel") {
						return {
							content: row.isFirstInGroup ? (
								<HoverTooltip
									style={{ display: "block", minWidth: 0, width: "100%", overflow: "hidden" }}
									shouldShow={element => {
										const textElement = element.firstElementChild as HTMLElement | null;
										return Boolean(textElement && textElement.scrollWidth > textElement.clientWidth);
									}}
									tooltipStyle={{ maxWidth: "520px" }}
									content={<span style={{ color: "#fff", fontSize: "12px", wordBreak: "break-word" }}>{row.channel}</span>}>
									<Typo
										sx={{
											...tableCellSx,
											fontFamily: "inherit",
											fontWeight: 600,
											display: "block",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}}>
										{row.channel}
									</Typo>
								</HoverTooltip>
							) : null,
							sx: noBorder,
						};
					}
					if (name === "ccy") {
						return { content: <Typo sx={{ ...tableCellSx, fontWeight: 700 }}>{row.ccy}</Typo>, sx: noBorder };
					}
					if (name === "txns") {
						return { content: <Typo sx={{ ...tableCellSx, textAlign: "end", display: "block" }}>{row.success}</Typo>, sx: noBorder };
					}
					if (name === "net") {
						return { content: <Typo sx={{ ...tableCellSx, fontWeight: 600, textAlign: "end", display: "block" }}>{row.net}</Typo>, sx: noBorder };
					}
					const valueColumns: Record<string, keyof IMerchantChannelRow> = {
						amount: "amount",
						refunds: "refunds",
						chargebacks: "chargebacks",
						failed: "failed",
						fees: "fees",
						rollings: "rollings",
						released: "release_rollings",
					};
					if (name && name in valueColumns) {
						return {
							content: <Typo sx={{ ...tableCellSx, textAlign: "end", display: "block" }}>{row[valueColumns[name]]}</Typo>,
							sx: noBorder,
						};
					}
					return { content: null, sx: noBorder };
				}}
				data={rows}
			/>
		</Box>
	);
};

const ReportsTable = ({ reports }: { reports: IStatementReport[]; clientId: number }) => {
	useLocale();
	const exitTransaction = useContext(TransactionNavigationContext);
	const sorted = [...reports].sort((a, b) => a.date.localeCompare(b.date));

	const rows: IReportRow[] = [];
	for (const r of sorted) {
		const entries = r.data.totals ? Object.entries(r.data.totals) : [];
		if (entries.length === 0) {
			rows.push({
				id: r.id,
				date: r.date,
				ccy: "—",
				success: "—",
				amount: "—",
				refunds: "—",
				chargebacks: "—",
				oct: "—",
				failed: "—",
				fees: "—",
				rollings: "—",
				release_rollings: "—",
				adj_in: "—",
				adj_out: "—",
				net: "—",
				hasTotals: false,
				isFirstInGroup: true,
				isLastInGroup: true,
			});
		} else {
			entries.forEach(([ccy, t], i) => {
				rows.push({
					id: r.id,
					date: r.date,
					ccy,
					success: String(t.count_success ?? 0),
					amount: formatAmount(t.total_amount ?? 0),
					refunds: formatAmount(t.total_refunds ?? 0),
					chargebacks: formatAmount(t.total_chargebacks ?? 0),
					oct: formatAmount(t.total_oct ?? 0),
					failed: formatAmount(t.total_failed ?? 0),
					fees: formatAmount(t.total_fees ?? 0),
					rollings: formatAmount(t.total_rollings ?? 0),
					release_rollings: formatAmount(t.total_release_rollings ?? 0),
					adj_in: formatAmount(t.total_ajustment_in ?? 0),
					adj_out: formatAmount(t.total_ajustment_out ?? 0),
					net: formatAmount(t.total_net ?? 0),
					hasTotals: true,
					isFirstInGroup: i === 0,
					isLastInGroup: i === entries.length - 1,
				});
			});
		}
	}

	const openReport = async (row: IReportRow) => {
		if (row.hasTotals) {
			exitTransaction?.();
			S_REPORT_POPUP.invoke({
				id: row.id,

				date: row.date,
			});
		}
	};

	return (
		<Box sx={{ display: "flex", minWidth: 0, width: "100%", overflowX: "auto" }}>
			<Table<IReportRow>
				headPadding="10px 12px"
				headSx={statementTableHeadSx}
				cellPadding="10px 12px"
				sx={{ width: "100%", fontVariantNumeric: "tabular-nums" }}
				onRowClick={row => openReport(row)}
				headerFactory={val => {
					if (typeof val === "string") {
						return {
							content: (
								<Typo
									variant="medium"
									sx={{
										fontWeight: 700,
										fontVariantNumeric: "tabular-nums",
										fontSize: "11px",
										textTransform: "uppercase",
										letterSpacing: "0.03em",
									}}>
									{(reportHeaderLabels[val] ?? val).toUpperCase()}
								</Typo>
							),
						};
					}
					return { content: null };
				}}
				headers={[
					{ value: "date", gridColumn: "max-content", sx: stickyHeadSx },
					{ value: "ccy", gridColumn: "max-content", sx: stickyHeadSx },
					{ value: "txns", gridColumn: "max-content", sx: stickyHeadRightSx },
					{ value: "amount", gridColumn: "auto", sx: stickyHeadRightSx },
					// { value: "adj in", gridColumn: "auto", sx: stickyHeadRightSx },
					// { value: "adj out", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "refunds", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "chargebacks", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "oct", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "failed", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "rollings", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "released", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "fees", gridColumn: "auto", sx: stickyHeadRightSx },
					{ value: "net", gridColumn: "auto", sx: stickyHeadRightSx },
				]}
				order={[
					"date" as any,
					"ccy" as any,
					"txns" as any,
					"amount" as any,
					// "adj in" as any,
					// "adj out" as any,
					"refunds" as any,
					"chargebacks" as any,
					"oct" as any,
					"failed" as any,
					"rollings" as any,
					"released" as any,
					"fees" as any,
					"net" as any,
				]}
				gridTemplateColumns="max-content max-content max-content auto auto auto auto auto auto auto auto auto"
				cellFactory={(row, _cell, _index, _key, name) => {
					const dimStyle = row.hasTotals ? {} : { opacity: 0.4 };
					const noBorder = !row.isLastInGroup ? { borderBottom: "1px solid transparent" } : {};
					if (name === "date") {
						return {
							content: row.isFirstInGroup ? <Typo sx={{ ...tableCellSx, ...dimStyle }}>{formatUtcDate(row.date)}</Typo> : null,
							sx: noBorder,
						};
					}
					if (name === "ccy") {
						return {
							content: <Typo sx={{ ...tableCellSx, ...dimStyle, fontWeight: 700 }}>{row.ccy}</Typo>,
							sx: noBorder,
						};
					}
					if (name === "txns") {
						return {
							content: <Typo sx={{ ...tableCellSx, ...dimStyle, textAlign: "end", display: "block" }}>{row.success}</Typo>,
							sx: noBorder,
						};
					}
					if (name === "net") {
						return {
							content: <Typo sx={{ ...tableCellSx, ...dimStyle, fontWeight: 600, textAlign: "end", display: "block" }}>{row.net}</Typo>,
							sx: noBorder,
						};
					}
					const valueColumns: Record<string, keyof IReportRow> = {
						amount: "amount",
						refunds: "refunds",
						chargebacks: "chargebacks",
						oct: "oct",
						failed: "failed",
						fees: "fees",
						rollings: "rollings",
						released: "release_rollings",
						"adj in": "adj_in",
						"adj out": "adj_out",
					};
					if (name && name in valueColumns) {
						return {
							content: <Typo sx={{ ...tableCellSx, ...dimStyle, textAlign: "end", display: "block" }}>{row[valueColumns[name]]}</Typo>,
							sx: noBorder,
						};
					}
					return { content: null, sx: noBorder };
				}}
				data={rows}
			/>
		</Box>
	);
};
